/**
 * CRM Auth — авторизация CRM-пользователей через таблицу crm_users.
 * - Сессия хранится в sessionStorage
 * - Данные пользователя (имя, роль) тоже в сессии
 * - Срок: 8 часов
 */

const SESSION_KEY = 'crm_session';
const SESSION_HOURS = 8;

// ─── CRM User types ──────────────────────────────────────────────────────────

export type CrmRole = 'admin' | 'head_manager' | 'manager';

export interface CrmSessionUser {
  id: string;
  name: string;
  username: string;
  role: CrmRole;
}

interface CrmSession {
  user: CrmSessionUser;
  exp: number;   // expires at (ms)
}

// ─── Session helpers ──────────────────────────────────────────────────────────

function purgeSession(): void {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
}

function readSession(): CrmSession | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const s: CrmSession = JSON.parse(raw);
    if (typeof s.exp !== 'number' || Date.now() > s.exp) {
      purgeSession();
      return null;
    }
    return s;
  } catch {
    purgeSession();
    return null;
  }
}

// ─── Proxy URL (same as adminDb) ──────────────────────────────────────────────

const PROXY_URL = 'https://98va6f1d--admin-db-proxy.functions.blink.new';

async function findUserByCredentials(
  username: string,
  password: string,
): Promise<CrmSessionUser | null> {
  try {
    const res = await fetch(PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'list',
        table: 'crmUsers',
        payload: { where: { username } },
      }),
    });
    const json = await res.json().catch(() => null);
    if (!json?.ok || !Array.isArray(json.data)) return null;

    const user = json.data.find(
      (u: any) => u.username === username && u.password === password,
    );
    if (!user) return null;

    return {
      id: user.id,
      name: user.name,
      username: user.username,
      role: user.role as CrmRole,
    };
  } catch {
    return null;
  }
}

// ─── Core API ─────────────────────────────────────────────────────────────────

/**
 * Логин: ищет пользователя в crm_users и создаёт сессию.
 */
export async function crmLogin(inputUsername: string, inputPassword: string): Promise<CrmSessionUser> {
  const username = inputUsername.trim();
  const password = inputPassword.trim();

  if (!username || !password) {
    throw new Error('Введите логин и пароль');
  }

  const user = await findUserByCredentials(username, password);
  if (!user) {
    throw new Error('Неверный логин или пароль');
  }

  const exp = Date.now() + SESSION_HOURS * 60 * 60 * 1000;
  const session: CrmSession = { user, exp };
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));

  return user;
}

/**
 * Выход: удалить сессию.
 */
export function crmLogout(): void {
  purgeSession();
}

/**
 * Синхронная проверка (для начального рендера).
 */
export function isCrmAuthedSync(): boolean {
  return readSession() !== null;
}

/**
 * Получить текущего пользователя из сессии (синхронно).
 */
export function getCrmCurrentUser(): CrmSessionUser | null {
  return readSession()?.user ?? null;
}

/**
 * Полная async проверка — для совместимости с хуком.
 */
export async function isCrmAuthed(): Promise<boolean> {
  return readSession() !== null;
}

/**
 * Оставшееся время сессии в мс. 0 если нет.
 */
export function crmSessionRemainingMs(): number {
  const s = readSession();
  return s ? Math.max(0, s.exp - Date.now()) : 0;
}
